import loadable from '@loadable/component';
import ChatWidget from 'components/ChatWidget/ChatWidget';
import React, { FC, Suspense, useEffect } from 'react';
import {
  BrowserRouter,
  Route,
  Routes as Switch,
  Navigate,
} from 'react-router-dom';
import { Page } from './types';
import { isValidToken, setSession } from 'utils/jwt';
import { useSelector, useDispatch } from 'react-redux';
import api from 'utils/api';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from 'react-hot-toast';

import { ROUTES } from './routes';
import PrivateRoute from './PrivateRoute';
import PublicOnlyRoute from './PublicOnlyRoute';

import ScrollToTop from './ScrollToTop';
import SiteSeo from 'components/Seo/SiteSeo';

import { FallbackComponent } from 'components/LoadingScreen/LoadingScreen';
import HUB_PATHS from 'pages/Destinations/paths.json';
const loadableOptions = { fallback: <FallbackComponent /> };

const PageHome = loadable(
  () => import('containers/PageHome/PageHome'),
  loadableOptions
);
const SignUpPage = loadable(
  () => import('pages/Auth/SignUpPage/SignUpV2Page'),
  loadableOptions
);
const SignUpGreetherPage = loadable(
  () => import('pages/Auth/SignUpPage/SignUpGreetherPage'),
  loadableOptions
);
const LoginPage = loadable(
  () => import('pages/Auth/LoginPage'),
  loadableOptions
);
const BookingStepOnePage = loadable(
  () => import('pages/Booking/StepOnePage/'),
  loadableOptions
);
const BookingStepTwoPage = loadable(
  () => import('pages/Booking/StepTwoPage/'),
  loadableOptions
);
const BookingStepThreePage = loadable(
  () => import('pages/Booking/StepThreePage/'),
  loadableOptions
);
const AdditionalTravelerRequirements = loadable(
  () => import('pages/Booking/AdditionalTravelerRequirements'),
  loadableOptions
);
const BookingConfirmed = loadable(
  () => import('pages/Booking/BookingConfirmedPage/BookingConfirmedPage'),
  loadableOptions
);
const BookingCheckoutPage = loadable(
  () => import('pages/Booking/CheckoutPage'),
  loadableOptions
);
const BookingProposalPage = loadable(
  () => import('pages/Booking/BookingProposalPage'),
  loadableOptions
);
const Page404 = loadable(
  () => import('containers/Page404/Page404'),
  loadableOptions
);
const FAQTravelerPage = loadable(
  () => import('pages/FAQPage/FAQTravelerPage'),
  loadableOptions
);
const FAQGreetherPage = loadable(
  () => import('pages/FAQPage/FAQGreetherPage'),
  loadableOptions
);
const PrivacyPolicy = loadable(
  () => import('pages/PrivacyPolicy/PrivacyPolicy'),
  loadableOptions
);
const TermsAndConditions = loadable(
  () => import('pages/TermsAndConditions/TermsAndConditions'),
  loadableOptions
);
const ConfirmEmailPage = loadable(
  () => import('pages/Auth/ConfirmationPage/ConfirmationPage')
);
const AwaitingConfirmationPage = loadable(
  () => import('pages/Auth/ConfirmationPage/AwaitingConfirmationPage'),
  loadableOptions
);
const CancellationPolicyPage = loadable(
  () => import('pages/FAQPage/CancellationPolicyPage'),
  loadableOptions
);
const NewPasswordPage = loadable(
  () => import('pages/Auth/NewPasswordPage/NewPasswordPage'),
  loadableOptions
);
const ListingExperiencesDetailPage = loadable(
  () => import('containers/ListingDetailPage/ListingExperiencesDetailPage'),
  loadableOptions
);
const ListingStayPage = loadable(
  () => import('containers/ListingStayPage/ListingStayPage'),
  loadableOptions
);
const UserPage = loadable(
  () => import('containers/UserPage/UserPage'),
  loadableOptions
);
const MyProfilePage = loadable(
  () => import('pages/MyProfilePage/MyProfilePage'),
  loadableOptions
);
const PageForgotPassword = loadable(
  () => import('containers/PageForgotPassword/PageForgotPassword'),
  loadableOptions
);

const DefineYourJourneyPage = loadable(
  () => import('pages/Auth/DefineYourJourneyPage/DefineYourJourneyV2Page'),
  loadableOptions
);

const DefineYourJourneyNextPage = loadable(
  () => import('pages/Auth/DefineYourJourneyPage/StepThreePage'),
  loadableOptions
);

const DefineYourJourneyFinishPage = loadable(
  () => import('pages/Auth/DefineYourJourneyPage/ConfirmateProfile'),
  loadableOptions
);

const SoloTravelGroupsPage = loadable(
  () => import('pages/SoloTravelGroups/SoloTravelGroupsPage'),
  loadableOptions
);

const SoloFemaleTravelPage = loadable(
  () => import('pages/SoloFemaleTravelPage/SoloFemaleTravelPage'),
  loadableOptions
);

const SoloTripsForWomenPage = loadable(
  () => import('pages/SoloTripsForWomenPage/SoloTripsForWomenPage'),
  loadableOptions
);

const SoloFemaleTravelerPage = loadable(
  () => import('pages/SoloFemaleTravelerPage/SoloFemaleTravelerPage'),
  loadableOptions
);

const MarrakeshPage = loadable(() => import('pages/Marrakesh/MarrakeshEnPage'));
const MarrakeshFrPage = loadable(
  () => import('pages/Marrakesh/MarrakeshFrPage')
);
const MarrakeshEsPage = loadable(
  () => import('pages/Marrakesh/MarrakeshEsPage')
);
const MarrakeshDePage = loadable(
  () => import('pages/Marrakesh/MarrakeshDePage')
);
const MarrakeshItPage = loadable(
  () => import('pages/Marrakesh/MarrakeshItPage')
);
const MarrakeshNlPage = loadable(
  () => import('pages/Marrakesh/MarrakeshNlPage')
);
const MarrakeshSvPage = loadable(
  () => import('pages/Marrakesh/MarrakeshSvPage')
);
const MarrakeshNoPage = loadable(
  () => import('pages/Marrakesh/MarrakeshNoPage')
);
const MarrakeshDaPage = loadable(
  () => import('pages/Marrakesh/MarrakeshDaPage')
);
const MarrakeshPtPage = loadable(
  () => import('pages/Marrakesh/MarrakeshPtPage')
);
// Hubs de destino nuevos: una página por ciudad que recibe el idioma por prop.
// Los slugs viven en paths.json, que también lee seo-routes.mjs.
const CITY_HUBS: Record<
  keyof typeof HUB_PATHS,
  React.ComponentType<{ locale: string }>
> = {
  istanbul: loadable(() => import('pages/Destinations/istanbul')),
  casablanca: loadable(() => import('pages/Destinations/casablanca')),
  tokyo: loadable(() => import('pages/Destinations/tokyo')),
  seoul: loadable(() => import('pages/Destinations/seoul')),
  rome: loadable(() => import('pages/Destinations/rome')),
};
const WomenApprovedHotelsPage = loadable(
  () => import('pages/WomenApprovedHotels/WomenApprovedHotelsPage')
);
const BrandKitPartnersPage = loadable(
  () => import('pages/BrandKitPartners/BrandKitPartnersPage')
);
const EarlyTesterPage = loadable(
  () => import('pages/EarlyTester/EarlyTesterPage')
);

const WhatWeDoPage = loadable(
  () => import('pages/WhatWeDo/WhatWeDoPage'),
  loadableOptions
);

const BlogPage = loadable(
  () => import('containers/BlogPage/BlogPage'),
  loadableOptions
);

const BlogDetailPage = loadable(
  () => import('pages/Blog/BlogDetailPage'),
  loadableOptions
);

type ExternalRedirectProps = {
  to: string;
};

export const pages: Page[] = [
  { path: '/', exact: true, component: PageHome as FC<any> },
  { path: '/#', exact: true, component: PageHome as FC<any> },
];

const Routes = () => {
  const dispatch = useDispatch();
  const tokenLocal = localStorage.getItem('accessToken');
  const hasToken = isValidToken(tokenLocal);
  const state = useSelector((state: any) => state.user);

  const getUserInformation = async () => {
    if (!tokenLocal) return;
    return await api('/auth/my-account', 'POST', {}, false, {
      status: true,
      access_token: tokenLocal,
    });
  };

  if (Object.keys(state).length === 0 && hasToken) {
    getUserInformation().then((res) => {
      dispatch({ type: 'loadAccount', payload: res });
    });
  } else if (!hasToken) {
    setSession('');
  }

  const userRole = state?.info?.account?.role || undefined;

  const ExternalRedirect: FC<ExternalRedirectProps> = ({ to }) => {
    useEffect(() => {
      window.location.href = to;
    }, [to]);

    return null;
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      <ChatWidget />
      <SiteSeo />
      <Suspense fallback={<FallbackComponent />}>
        <Toaster position="top-right" reverseOrder={false} />
        <AnimatePresence>
          <Switch>
            {pages.map(({ component, path, exact }) => {
              return <Route key={path} Component={component} path={path} />;
            })}
            <Route
              path="/login"
              element={
                <PublicOnlyRoute>
                  <LoginPage />
                </PublicOnlyRoute>
              }
            />
            <Route
              path={ROUTES.SIGNUP}
              element={
                <PublicOnlyRoute>
                  <SignUpPage />
                </PublicOnlyRoute>
              }
            />
            <Route
              path={ROUTES.SIGN_UP_GREETER}
              element={
                <PublicOnlyRoute>
                  <SignUpPage />
                </PublicOnlyRoute>
              }
            />
            <Route
              path={ROUTES.SIGN_UP_TRAVELER}
              element={
                <PublicOnlyRoute>
                  <SignUpPage />
                </PublicOnlyRoute>
              }
            />
            <Route
              path={ROUTES.SIGNUP_GREETER}
              element={
                <PublicOnlyRoute>
                  <SignUpGreetherPage />
                </PublicOnlyRoute>
              }
            />
            <Route
              path={ROUTES.AWAITINGCONFIRMATION}
              element={
                <PublicOnlyRoute>
                  <AwaitingConfirmationPage />
                </PublicOnlyRoute>
              }
            />
            <Route
              path={ROUTES.CONFIRMEMAIL}
              element={
                <PublicOnlyRoute>
                  <ConfirmEmailPage />
                </PublicOnlyRoute>
              }
            />
            <Route
              path={ROUTES.NEW_PASSWORD}
              element={
                <PublicOnlyRoute>
                  <NewPasswordPage />
                </PublicOnlyRoute>
              }
            />
            <Route path={ROUTES.FAQGREETER} element={<FAQGreetherPage />} />
            <Route path={ROUTES.FAQTRAVELER} element={<FAQTravelerPage />} />
            <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicy />} />
            <Route
              path={ROUTES.TERMS_AND_CONDITIONS}
              element={<TermsAndConditions />}
            />
            <Route
              path={ROUTES.CANCELLATIONPOLICY}
              element={<CancellationPolicyPage />}
            />
            <Route
              path={ROUTES.BOOKINGSTEPONE}
              element={<BookingStepOnePage />}
            />
            <Route
              path={ROUTES.BOOKINGSTEPTWO}
              element={
                <PrivateRoute>
                  <BookingStepTwoPage />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.ADDITIONAL_REQUIREMENTS}
              element={
                <PrivateRoute>
                  <AdditionalTravelerRequirements />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.BOOKINGSTEPTHREE}
              element={
                <PrivateRoute>
                  <BookingStepThreePage />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.BOOKING_PROPOSAL}
              element={<BookingProposalPage />}
            />
            <Route
              path={ROUTES.CHECKOUT}
              element={
                <PrivateRoute>
                  <BookingCheckoutPage />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.BOOKINGCONFIRMED}
              element={
                <PrivateRoute>
                  <BookingConfirmed />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.QUESTIONNAIRE}
              element={
                <PrivateRoute>
                  <DefineYourJourneyPage userRole={userRole} />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.QUESTIONNAIRENEXT}
              element={
                <PrivateRoute>
                  <DefineYourJourneyNextPage userRole={userRole} />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.QUESTIONNAIREFINISH}
              element={
                <PrivateRoute>
                  <DefineYourJourneyFinishPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                hasToken ? <Navigate replace to="/" /> : <PageForgotPassword />
              }
            />
            <Route
              path="/my-profile"
              element={
                hasToken ? <MyProfilePage /> : <Navigate replace to="/" />
              }
            />
            <Route
              path="/p/:userId"
              element={
                hasToken ? <UserPage userRole={userRole} /> : <SignUpPage />
              }
            />
            <Route
              path="/b/:userName"
              element={
                hasToken ? <ListingExperiencesDetailPage /> : <SignUpPage />
              }
            />
            <Route
              path="/search-greeters"
              element={hasToken ? <ListingStayPage /> : <SignUpPage />}
            />
            <Route
              path={ROUTES.SOLO_TRAVEL_GROUPS}
              element={<SoloTravelGroupsPage />}
            />
            <Route
              path={ROUTES.SOLO_FEMALE_TRAVEL}
              element={<SoloFemaleTravelPage />}
            />
            <Route
              path={ROUTES.SOLO_FEMALE_TRAVELER}
              element={<SoloFemaleTravelerPage />}
            />
            <Route
              path={ROUTES.SOLO_TRIPS_FOR_WOMEN}
              element={<SoloTripsForWomenPage />}
            />
            <Route path={ROUTES.MARRAKESH} element={<MarrakeshPage />} />
            <Route path={ROUTES.MARRAKESH_FR} element={<MarrakeshFrPage />} />
            <Route path={ROUTES.MARRAKESH_ES} element={<MarrakeshEsPage />} />
            <Route path={ROUTES.MARRAKESH_DE} element={<MarrakeshDePage />} />
            <Route path={ROUTES.MARRAKESH_IT} element={<MarrakeshItPage />} />
            <Route path={ROUTES.MARRAKESH_NL} element={<MarrakeshNlPage />} />
            <Route path={ROUTES.MARRAKESH_SV} element={<MarrakeshSvPage />} />
            <Route path={ROUTES.MARRAKESH_NO} element={<MarrakeshNoPage />} />
            <Route path={ROUTES.MARRAKESH_DA} element={<MarrakeshDaPage />} />
            <Route path={ROUTES.MARRAKESH_PT} element={<MarrakeshPtPage />} />
            {Object.entries(HUB_PATHS).flatMap(([city, paths]) => {
              const Hub = CITY_HUBS[city as keyof typeof HUB_PATHS];
              return Object.entries(paths).map(([locale, path]) => (
                <Route key={path} path={path} element={<Hub locale={locale} />} />
              ));
            })}
            <Route
              path={ROUTES.WOMEN_APPROVED_HOTELS}
              element={<WomenApprovedHotelsPage />}
            />
            <Route
              path={ROUTES.BRAND_KIT_PARTNERS}
              element={<BrandKitPartnersPage />}
            />
            <Route path={ROUTES.EARLY_TESTER} element={<EarlyTesterPage />} />
            <Route path="/what-we-do" element={<WhatWeDoPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route
              path="/contact"
              element={
                <ExternalRedirect to="https://www.hello.greether.com/contactgreether" />
              }
            />
            <Route path="*" element={<Page404 />} />
          </Switch>
        </AnimatePresence>
      </Suspense>
    </BrowserRouter>
  );
};

export default Routes;
